import React, { useEffect, useState } from "react";
import { fxApi, formatApiError } from "../../lib/ledgerApi";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import HashString from "../../components/HashString";
import { toast } from "sonner";
import { Globe2, RefreshCw, Banknote, Sliders, Trash2 } from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "AED"];

const fmt = (s, d = 6) => {
  try { const n = Number(s); if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: d }); } catch {}
  return s;
};

export default function AdminFX() {
  const [snap, setSnap] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [audit, setAudit] = useState([]);

  const [overrideCcy, setOverrideCcy] = useState("EUR");
  const [overrideRate, setOverrideRate] = useState("");
  const [busy, setBusy] = useState(false);

  const [neoBaseUsd, setNeoBaseUsd] = useState("");
  const [spreadBps, setSpreadBps] = useState("");

  const load = async () => {
    try {
      const [s, t, a] = await Promise.all([
        fxApi.rates(),
        fxApi.adminTreasury(),
        fxApi.adminAudit(),
      ]);
      setSnap(s); setTreasury(t); setAudit(a);
      if (t?.neo_base_usd) setNeoBaseUsd(t.neo_base_usd);
      if (t?.spread_bps) setSpreadBps(t.spread_bps);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, []);

  const onRefresh = async () => {
    setBusy(true);
    try { await fxApi.adminRefresh(); toast.success("Rates refreshed"); await load(); }
    catch (e) { toast.error("Refresh failed", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const onOverride = async () => {
    setBusy(true);
    try {
      await fxApi.adminOverride(overrideCcy, overrideRate);
      toast.success(`${overrideCcy} override set to ${overrideRate}`);
      setOverrideRate("");
      await load();
    } catch (e) { toast.error("Override failed", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const onClearOverride = async (c) => {
    if (!window.confirm(`Clear override for ${c}?`)) return;
    setBusy(true);
    try { await fxApi.adminClearOverride(c); toast.success(`Cleared ${c} override`); await load(); }
    catch (e) { toast.error("Clear failed", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const onSaveConfig = async () => {
    setBusy(true);
    try {
      await fxApi.adminConfig({ neo_base_usd: neoBaseUsd, spread_bps: spreadBps });
      toast.success("Network config saved");
      await load();
    } catch (e) { toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  const treasuryNeo = treasury?.wallets?.find((w) => w.currency === "NEO");

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Super-admin · FX</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Globe2 className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            FX controls
          </h1>
        </div>
        <Button onClick={onRefresh} disabled={busy} variant="outline" data-testid="fx-refresh-btn" className="rounded-sm border-border hover:border-foreground">
          <RefreshCw className="w-4 h-4 mr-1" strokeWidth={1.5} /> Force refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        {/* Treasury */}
        <div className="lg:col-span-1 p-5 border border-border rounded-sm bg-foreground text-background">
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-60">Network treasury (NEO)</div>
          <div className="font-mono text-3xl mt-1" data-testid="treasury-neo-balance">
            {treasuryNeo ? fmt(treasuryNeo.balance, 4) : "—"}
          </div>
          <div className="text-[11px] opacity-60 mt-1">{treasury?.business?.legal_name}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-60 mt-4">Spread income (recent)</div>
          <div className="text-xs mt-1">
            {treasury?.income?.length ?? 0} entries
          </div>
        </div>

        {/* Network config */}
        <div className="lg:col-span-2 p-5 border border-border rounded-sm bg-card">
          <h2 className="text-sm font-medium mb-3 inline-flex items-center gap-2"><Sliders className="w-4 h-4" strokeWidth={1.5} /> Network config</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">NEO base USD</Label>
              <Input data-testid="cfg-neo-base-usd" value={neoBaseUsd} onChange={(e) => setNeoBaseUsd(e.target.value)} className="rounded-sm font-mono mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Spread (bps, 0-1000)</Label>
              <Input data-testid="cfg-spread-bps" value={spreadBps} onChange={(e) => setSpreadBps(e.target.value)} className="rounded-sm font-mono mt-1" />
            </div>
          </div>
          <Button onClick={onSaveConfig} disabled={busy} data-testid="cfg-save" className="mt-3 rounded-sm">
            Save config
          </Button>
          <p className="text-[11px] text-muted-foreground mt-2">
            Spread (default 50bps = 0.5%) is taken in NEO and routed to Network Treasury.
          </p>
        </div>
      </div>

      {/* Rates with override controls */}
      <h2 className="text-lg font-medium mt-10 mb-3">Live rates &amp; manual overrides</h2>
      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border bg-muted/30">
          <div className="col-span-2">CCY</div>
          <div className="col-span-3 text-right">Effective</div>
          <div className="col-span-3 text-right">Override</div>
          <div className="col-span-4 text-right">Action</div>
        </div>
        {snap && Object.entries(snap.rates).map(([c, r]) => {
          const ovr = snap.overrides?.find((o) => o.currency === c);
          return (
            <div key={c} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 items-center font-mono text-sm">
              <div className="col-span-2 font-medium">{c}</div>
              <div className="col-span-3 text-right">{fmt(r, 6)}</div>
              <div className="col-span-3 text-right text-[11px]">
                {ovr ? <span className="text-status-info">{fmt(ovr.rate)} · {ovr.set_by}</span> : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="col-span-4 text-right">
                {ovr && c !== "USD" && (
                  <button data-testid={`clear-ovr-${c}`} onClick={() => onClearOverride(c)} className="text-[11px] text-status-tampered hover:underline inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} /> clear
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add override */}
      <div className="mt-4 p-4 border border-border rounded-sm bg-card flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Currency</Label>
          <Select value={overrideCcy} onValueChange={setOverrideCcy}>
            <SelectTrigger data-testid="ovr-currency" className="rounded-sm font-mono mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">New rate (per 1 USD)</Label>
          <Input data-testid="ovr-rate" value={overrideRate} onChange={(e) => setOverrideRate(e.target.value)} placeholder="0.92" className="rounded-sm font-mono mt-1" />
        </div>
        <Button onClick={onOverride} disabled={busy || !overrideRate} data-testid="ovr-submit" className="rounded-sm">
          Set override
        </Button>
      </div>

      {/* Audit log */}
      <h2 className="text-lg font-medium mt-10 mb-3">Audit log</h2>
      <div className="border border-border rounded-sm bg-card divide-y divide-border max-h-80 overflow-auto">
        {audit.length === 0 && <div className="p-4 text-sm text-muted-foreground">No overrides have been applied.</div>}
        {audit.map((a, i) => (
          <div key={i} className="p-3 grid grid-cols-12 gap-3 items-center font-mono text-xs">
            <div className="col-span-2">{a.currency}</div>
            <div className="col-span-2">{a.rate ?? <span className="text-muted-foreground">cleared</span>}</div>
            <div className="col-span-4 truncate">{a.set_by}</div>
            <div className="col-span-4 text-right text-muted-foreground">{new Date(a.set_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
